import { IntervalContainer } from "@Components/forecast/hourly/IntervalContainer";
import { ScrollButton } from "@Components/forecast/hourly/ScrollButton";
import WindLine from "@Components/forecast/hourly/WindLine";
import { LegendLabel } from "@Components/forecast/label/LegendLabel";
import {
  getTemperatureLegend,
  SNOW_LEGEND,
  WIND_LEGEND,
} from "@Constants/legend";
import { useForecastAppContext } from "@Context/ForecastAppContext/ForecastAppContext";
import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import { ScrollHandler } from "@Hooks/useScrollInteractions";
import { Direction } from "@Models/Direction";
import {
  ForwardedRef,
  forwardRef,
  RefObject,
  SyntheticEvent,
  useState,
} from "react";

export type Representation = "temperature" | "wind" | "snow";

type NavigationContainerProps = {
  representation: Representation;
  dayRefs: React.RefObject<
    Record<Representation, RefObject<HTMLLIElement | null>[]>
  >;
  canNavigateLeft: boolean;
  canNavigateRight: boolean;
  navigateLeft: () => void;
  navigateRight: () => void;
  handleScroll: ScrollHandler;
  handleMouseWheelScroll: ScrollHandler;
};

export const NavigationContainer = forwardRef(
  (
    {
      representation,
      dayRefs,
      canNavigateLeft,
      canNavigateRight,
      navigateLeft,
      navigateRight,
      handleScroll,
      handleMouseWheelScroll,
    }: NavigationContainerProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { intervals } = useForecastAppContext();
    const { t } = useTranslations();
    const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false);

    const legendItems =
      representation === "temperature"
        ? getTemperatureLegend(intervals[0])
        : representation === "wind"
          ? WIND_LEGEND
          : SNOW_LEGEND;

    // Only show snow if there is snow data available
    const visible =
      representation !== "snow" ||
      (representation === "snow" &&
        intervals[0] &&
        typeof intervals[0].FRESHSNOW_MM === "number");

    if (!visible) {
      return null;
    }

    const innerHandleScroll = (e: SyntheticEvent<HTMLElement>) => {
      handleScroll(e, ref as RefObject<HTMLDivElement | null> | null);
    };

    const innerHandleMouseWheelScroll = (e: SyntheticEvent<HTMLElement>) => {
      handleMouseWheelScroll(e, ref as RefObject<HTMLDivElement | null> | null);
    };

    return (
      <div>
        <section className="m:px-4 m:pt-10 m:pb-1 m:md:px-8 m:md:pt-7 m:lg:pt-9 m:xl:px-10">
          <h2 className="m:m-0 m:p-0 m:text-2xl m:leading-5 m:font-medium m:tracking-0.2 m:text-blue-950 m:antialiased m:lg:text-4xl m:lg:leading-[25px] m:dark:text-neutral-white">
            {t(`forecast.${representation}`)}
          </h2>
        </section>
        <div
          className="m:relative m:ml-0 m:flex m:w-auto m:flex-row m:lg:-mx-20 m:xl:-mx-[70px]"
          onBlur={() => setIsScrollButtonVisible(false)}
          onFocus={() => setIsScrollButtonVisible(true)}
          onMouseEnter={() => setIsScrollButtonVisible(true)}
          onMouseLeave={() => setIsScrollButtonVisible(false)}>
          <div className="m:z-3 m:hidden m:bg-scroll-blocker-start m:lg:absolute m:lg:-left-2 m:lg:z-10 m:lg:block m:lg:h-full m:lg:w-[120px] m:dark:bg-scroll-blocker-start-dark"></div>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            data-testid={`hourly-navigation-${representation}`}
            data-scroll-position="0"
            tabIndex={-1}
            className="m:scrollbar-hidden m:flex m:w-full m:flex-row m:overflow-hidden m:overflow-x-auto m:px-4 m:pt-5 m:md:px-8 m:lg:px-28"
            onWheel={innerHandleMouseWheelScroll}
            onTouchEnd={innerHandleScroll}
            onMouseUp={innerHandleScroll}
            onKeyUp={innerHandleScroll}
            ref={ref}>
            <ul className="m:flex m:snap-none m:list-none m:flex-row m:scroll-auto">
              <div className="m:relative m:flex m:flex-row m:lg:pt-1 m:xl:pt-2">
                {representation === "wind" && (
                  <WindLine intervals={intervals} />
                )}
                <IntervalContainer
                  intervals={intervals}
                  representation={representation}
                  dayRefs={dayRefs}
                />
              </div>
            </ul>
          </div>
          <div className="m:z-2 m:hidden m:lg:absolute m:lg:top-0 m:lg:-right-0 m:lg:block m:lg:h-full m:lg:w-[120px] m:lg:bg-scroll-blocker-end m:xl:-right-2 m:lg:dark:bg-scroll-blocker-end-dark"></div>
          <div className="m:absolute m:top-1/2 m:right-0 m:flex m:flex-row m:gap-px m:lg:right-20 m:xl:right-[72px]">
            {(["left", "right"] as Direction[]).map((direction) => (
              <ScrollButton
                key={direction}
                isVisible={isScrollButtonVisible}
                direction={direction}
                disabled={
                  direction === "left" ? !canNavigateLeft : !canNavigateRight
                }
                onClick={direction === "left" ? navigateLeft : navigateRight}
              />
            ))}
          </div>
        </div>
        <LegendLabel legendItems={legendItems} />
      </div>
    );
  },
);

NavigationContainer.displayName = "NavigationContainer";
